/**
 * Rating Widget for Recommendations
 *
 * 4-level numeric button row [0] [1] [2] [3] following the project's
 * button-group styling pattern (similar to keyword/semantic toggle
 * in KnowledgePage). Each level gets a color-coded background:
 *   0 = muted gray (irrelevant)
 *   1 = accent-red (poor)
 *   2 = accent-yellow (useful)
 *   3 = accent-green (excellent)
 *
 * The numeric scale (vs stars or thumbs) was chosen for precision —
 * it maps directly to the backend SMALLINT(0-3) without ambiguity.
 */

import type { RecommendationRating } from "../../types/recommendation";
import { RATING_LABELS } from "../../types/recommendation";

interface RatingWidgetProps {
  /** Current rating value (null if unrated) */
  value: RecommendationRating | null;
  /** Called when user clicks a rating button */
  onChange: (rating: RecommendationRating) => void;
  /** Disable interaction (e.g., during API call) */
  disabled?: boolean;
}

/** Color config per rating value — bg for selected, text always visible */
const RATING_COLORS: Record<
  RecommendationRating,
  { selected: string; text: string }
> = {
  0: { selected: "bg-text-muted/20", text: "text-text-muted" },
  1: { selected: "bg-accent-red/20", text: "text-accent-red" },
  2: { selected: "bg-accent-yellow/20", text: "text-accent-yellow" },
  3: { selected: "bg-accent-green/20", text: "text-accent-green" },
};

export function RatingWidget({ value, onChange, disabled }: RatingWidgetProps) {
  const ratings: RecommendationRating[] = [0, 1, 2, 3];

  return (
    <div
      className="flex items-center gap-0.5"
      role="radiogroup"
      aria-label="Rate recommendation"
    >
      {ratings.map((rating) => {
        const isSelected = value === rating;
        const colors = RATING_COLORS[rating];

        return (
          <button
            key={rating}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={RATING_LABELS[rating]}
            title={RATING_LABELS[rating]}
            disabled={disabled}
            onClick={() => onChange(rating)}
            className={`
              w-7 h-7 rounded text-xs font-mono font-semibold
              transition-all duration-150 border
              ${
                isSelected
                  ? `${colors.selected} ${colors.text} border-current`
                  : "bg-bg-primary border-border text-text-muted hover:bg-bg-hover"
              }
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            {rating}
          </button>
        );
      })}
    </div>
  );
}
